/**
 * DeactivateAccountDialog — the customer switches their own account off.
 *
 * Two steps: explain what deactivation does, then confirm with a fresh OTP to
 * the account's own mobile number. The backend only accepts a Firebase ID
 * token from a sign-in at most two minutes old, so the code is sent here, not
 * reused from login.
 *
 * The OTP runs on `stepUpAuth`, a separate Firebase Auth instance, so the
 * phone sign-in never replaces the custom-token session chat and
 * notifications use. That matters when deactivation fails and the user stays
 * signed in.
 *
 * On success the account is refused everywhere, so we sign out locally and go
 * to /login, where a sign-in attempt shows the backend's "deactivated" message.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { signOut } from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@finmin/auth';
import { isApiError } from '@finmin/api-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorMessage,
  OtpInput,
  toast,
} from '@finmin/ui';
import { useDeactivateAccount } from '@/api/account';
import { queryClient } from '@/api/query-client';
import { stepUpAuth } from '@/lib/firebase';
import { sendFirebaseOtp, verifyFirebaseOtp } from '@/lib/firebase-phone-auth';
import { useOtpTimer } from '@/features/auth/hooks/use-otp-timer';
import { maskPhone } from '@/features/auth/utils/phone';

type Step = 'confirm' | 'otp';

interface Props {
  /** The account's own mobile number (E.164). The OTP must come from it. */
  mobile: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeactivateAccountDialog({ mobile, open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const resendTimer = useOtpTimer();
  const deactivate = useDeactivateAccount();

  const [step, setStep] = React.useState<Step>('confirm');
  const [otp, setOtp] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const busy = sending || verifying;

  // Start from the explanation every time the dialog is (re)opened.
  React.useEffect(() => {
    if (open) {
      setStep('confirm');
      setOtp('');
      setError(null);
      resendTimer.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function sendOtp() {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await sendFirebaseOtp(mobile, stepUpAuth);
      setOtp('');
      setStep('otp');
      resendTimer.start();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the code. Please try again.');
    } finally {
      setSending(false);
    }
  }

  function signOutEverywhere() {
    onOpenChange(false);
    logout();
    queryClient.clear();
    void navigate({ to: '/login', replace: true });
  }

  async function confirm(code: string) {
    if (verifying || code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      let idToken: string;
      try {
        idToken = await verifyFirebaseOtp(code);
      } catch {
        setOtp('');
        setError('That code is incorrect or has expired. Check it, or send a new one.');
        return;
      }
      // The phone session's only job was minting this token; the token stays
      // valid after sign-out.
      void signOut(stepUpAuth).catch(() => undefined);

      try {
        await deactivate.mutateAsync({ id_token: idToken });
        toast.success('Account deactivated', 'You have been signed out.');
        signOutEverywhere();
      } catch (err) {
        if (isApiError(err) && err.code === 'account_deactivated') {
          signOutEverywhere();
          return;
        }
        setOtp('');
        setError(
          isApiError(err) ? err.message : 'Could not deactivate your account. Please try again.',
        );
      }
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        {/* Host for the invisible reCAPTCHA that Firebase Phone Auth renders. */}
        <div id="recaptcha-container" />

        <DialogHeader>
          <DialogTitle>Deactivate your account?</DialogTitle>
          <DialogDescription>
            {step === 'confirm'
              ? 'You will be signed out and will not be able to sign in again.'
              : `Enter the 6-digit code we sent to ${maskPhone(mobile)}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'confirm' ? (
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>Your services, documents and payment records are kept.</li>
            <li>To use FinminOS again, contact support to restore your account.</li>
          </ul>
        ) : (
          <div className="space-y-3">
            <OtpInput
              value={otp}
              onChange={setOtp}
              onComplete={(v) => void confirm(v)}
              disabled={verifying}
              error={Boolean(error)}
              label="Verification code"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {resendTimer.isActive ? (
                `Resend code in ${resendTimer.secondsLeft}s`
              ) : (
                <button
                  type="button"
                  onClick={() => void sendOtp()}
                  disabled={busy}
                  className="font-medium text-foreground underline underline-offset-2 disabled:opacity-60"
                >
                  Resend code
                </button>
              )}
            </p>
          </div>
        )}

        <ErrorMessage message={error} />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          {step === 'confirm' ? (
            <Button type="button" variant="destructive" onClick={() => void sendOtp()} disabled={busy}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Send code
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              onClick={() => void confirm(otp)}
              disabled={busy || otp.length !== 6}
            >
              {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Deactivate account
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
